import { createSlice, type PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import type { User } from "../../types/user";
import { getCurrentUserApi, getUsersApi, logoutApi, refreshTokenApi } from "../../api/usersApi";

interface UserState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  users: User[];
  usersLoading: boolean;
  usersError: string | null;
}

const initialState: UserState = {
  user: null,
  token: localStorage.getItem("token"), // Initialize from localStorage
  isAuthenticated: !!localStorage.getItem("token"),
  loading: false,
  error: null,
  users: [],
  usersLoading: false,
  usersError: null,
};

// Thunk to fetch all users (defined before slice to use in extraReducers)
export const fetchUsers = createAsyncThunk<User[], void, { rejectValue: string }>(
  "user/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      return await getUsersApi();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch users");
    }
  },
);

export const performLogout = createAsyncThunk("user/performLogout", async (_, { dispatch }) => {
  try {
    await logoutApi();
  } catch {
    // Ignore logout errors to avoid blocking local cleanup
  }
  dispatch(logout());
});

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.error = null;
      // Store token in localStorage
      localStorage.setItem("token", action.payload.token);
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      // Remove token from localStorage
      localStorage.removeItem("token");
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    clearUserError: (state) => {
      state.error = null;
    },
    clearUsersError: (state) => {
      state.usersError = null;
    },
    refreshTokenSuccess: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      // Store token in localStorage
      localStorage.setItem("token", action.payload.token);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.usersLoading = true;
        state.usersError = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<User[]>) => {
        state.usersLoading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload || "Failed to fetch users";
      });
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  setUser,
  clearUserError,
  clearUsersError,
  refreshTokenSuccess,
} = userSlice.actions;

// Thunk to fetch user data
export const fetchUserData = createAsyncThunk("user/fetchUserData", async (_, { dispatch }) => {
  const token = localStorage.getItem("token");

  if (!token) {
    return;
  }

  try {
    const response = await getCurrentUserApi();
    dispatch(setUser(response.user));
  } catch (error: any) {
    // Only clear token if it's an authentication error
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      dispatch(performLogout());
    }
  }
});

// Thunk to refresh token
export const refreshToken = createAsyncThunk(
  "user/refreshToken",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await refreshTokenApi();
      dispatch(
        refreshTokenSuccess({
          user: response.data.user,
          token: response.data.token,
        }),
      );
      return response.data;
    } catch (error: any) {
      // If refresh fails, logout the user
      localStorage.removeItem("token");
      dispatch(performLogout());
      return rejectWithValue(error.response?.data?.message || "Token refresh failed");
    }
  },
);

export default userSlice.reducer;
